import { useState } from "react";
import { usePostHog } from "posthog-js/react";
import { Loader2 } from "lucide-react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { useStore } from "store";
import { VisiblePostCodeType, StoreType } from "store/types";
import { DMDocumentFormats, DMDocumentNames } from "shared/constants";
import { LocationLatLngType } from "components/place-autocomplete/type";
import { PlaceAutocomplete } from "components/place-autocomplete";
import { PossibleFlyerType } from "pages/flyer/builder/drawer";
import { FlyerTypeSelect } from "components/FlyerTypeSelect";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";

interface EDDMEmptyStateProps {
  countryCode: string;
  disablePlaceAutocomplete: boolean;
  handleAutocompleteChanged: (newMarker: any) => void;
  getFilteredPostCodes?: (key: string) => VisiblePostCodeType[];
  setDrawingMode: (drawingMode: null | string) => void;
}

export const EDDMEmptyStateV2: React.FC<EDDMEmptyStateProps> = ({
  countryCode,
  disablePlaceAutocomplete,
  handleAutocompleteChanged,
  getFilteredPostCodes,
  setDrawingMode,
}) => {
  const posthog = usePostHog();
  const postHogTracker = new EmitAnalyticsEvent(posthog);
  const {
    user,
    campaign: { id: campaignId, flyerType },
    client: { name: clientName },
    updateCampaign,
    setSuggestionsEnabled,
  }: StoreType = useStore();
  const [location, setLocation] = useState<LocationLatLngType | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const submitDisabled = !location;

  const onPlaceChanged = (newLocation: LocationLatLngType) => {
    if (!newLocation) return;
    setLocation(newLocation);
  };

  const onSubmit = async () => {
    if (submitDisabled) return;
    setSubmitting(true);

    postHogTracker.run({
      eventName: "Clicked to explore map",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: { campaignId },
    });

    await updateCampaignExtraData({
      campaignId,
      flyerType,
    });
    // Audience attributes and route suggestions live on the map panel ("Suggest routes").
    setSuggestionsEnabled(false);
    setDrawingMode(null); // Route tool by default; Area is opt-in from the toolbar
    handleAutocompleteChanged(location);
    setSubmitting(false);
  };

  const handleChangeFlyerType = (newFlyerType: PossibleFlyerType) => {
    postHogTracker.run({
      eventName: "Changed postcard format before map navigation",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        postcardFormat: DMDocumentNames[newFlyerType as keyof typeof DMDocumentNames],
      },
    });
    updateCampaign({ flyerType: newFlyerType });
  };

  // Scrim fills its positioned parent (the map column) — never the viewport, or it would cover the sidebar.
  return (
    <div className="cb-v2-root absolute inset-0 z-[9] flex items-center justify-center bg-black/10 px-4">
      <Card className="flex w-[548px] max-w-full flex-col gap-5 border-solid p-[25px]" data-cy={"chooseLocationModal"}>
        <div className="flex flex-col gap-1">
          <span
            className="text-lg font-semibold leading-tight text-[#222336] dark:text-foreground"
            data-cy={"chooseLocationModalTitle"}
          >
            Create your audience
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label data-cy={"chooseLocationModalSubtitle"}>
            Select location <span className="text-destructive">*</span>
          </Label>
          <PlaceAutocomplete
            countryCode={countryCode}
            disabled={disablePlaceAutocomplete}
            dataCy={"chooseLocationModalFirstLocationInput"}
            placeholder={"Enter a city, neighborhood, or specific address"}
            postCodeSearch={true}
            onPlaceChanged={onPlaceChanged}
            getFilteredPostCodes={getFilteredPostCodes}
            disableAutoClear
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label data-cy={"chooseLocationModalPostcardFormat"}>
            Postcard format <span className="text-destructive">*</span>
          </Label>
          <FlyerTypeSelect
            handleChangeFlyerType={handleChangeFlyerType}
            flyerType={flyerType as keyof typeof DMDocumentFormats}
            scrollable
          />
        </div>

        <Button
          type="button"
          className="w-full"
          disabled={submitDisabled}
          onClick={onSubmit}
          data-cy={"chooseLocationModalExploreMapButton"}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Explore map
        </Button>
      </Card>
    </div>
  );
};
